import { NavLink } from 'react-router-dom';
import { cn } from '../../utils/classNames';
import { useAuth } from '../../hooks/useAuth';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import {
  DashboardIcon,
  ListIcon,
  ChatIcon,
  AIIcon,
  SettingsIcon,
  GlobeIcon,
  PlugIcon,
  CodeIcon,
  UsersIcon,
  CardIcon,
  QuizIcon,
  SparkleIcon,
  LogoutIcon,
  CloseIcon,
} from '../../icons';

// Sidebar nav definition. Groups mirror v3 shell.jsx layout but map onto the 11
// real routes from docs/react-migration/00-architecture.md §4.1.
const NAV_GROUPS = [
  {
    label: 'Аналитика',
    icon: DashboardIcon,
    items: [
      { to: '/overview', label: 'Обзор', icon: DashboardIcon },
      { to: '/sessions', label: 'Сессии', icon: ListIcon },
      { to: '/ai-consultant', label: 'ИИ-консультант', icon: ChatIcon },
    ],
  },
  {
    label: 'Настройка',
    icon: SettingsIcon,
    items: [
      { to: '/settings', label: 'Настройки', icon: SettingsIcon },
      { to: '/quiz', label: 'Квиз', icon: QuizIcon, badge: 'NEW' },
      { to: '/presets', label: 'Квиз и офферы', icon: SparkleIcon, badge: 'NEW' },
      { to: '/embed', label: 'Встраивание', icon: AIIcon },
    ],
  },
  {
    label: 'Подключение',
    icon: PlugIcon,
    items: [
      { to: '/domains', label: 'Домены', icon: GlobeIcon },
      { to: '/integrations', label: 'Интеграции', icon: CodeIcon },
    ],
  },
  {
    label: 'Деньги и доступ',
    icon: CardIcon,
    items: [
      { to: '/billing', label: 'Биллинг', icon: CardIcon },
      { to: '/clients', label: 'Клиенты', icon: UsersIcon },
    ],
  },
];

// Initials for the footer avatar. Mirrors vanilla updateSidebarIdentity(), which
// shows user.email as the identity. We derive 1–2 letters from the email local-part
// (or a name, if one is ever present): "john.doe@x" → "JD", "anna@x" → "AN".
function userInitials(user) {
  const source = (user?.name || user?.email || '').trim();
  if (!source) return '?';
  const local = source.split('@')[0];
  const parts = local.split(/[.\-_\s]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

export function Sidebar({ mobileOpen = false, onMobileClose }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Hide the "Клиенты" entry (and any other admin-only items) for non-admins,
  // mirroring vanilla updateSuperadminVisibility(). We drop empty groups too so
  // a group heading never renders with zero items.
  const navGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.to !== '/clients' || isAdmin),
  })).filter((group) => group.items.length > 0);

  return (
    <aside className={cn('lk-side', mobileOpen && 'lk-side--open')} aria-label="Основная навигация">
      <div className="lk-side__brand">
        <div className="lk-side__logo">LK</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="lk-side__brand-name">LeadKitchen</div>
          <div className="lk-side__brand-sub">Кабинет виджета</div>
        </div>
        {onMobileClose && (
          <button
            type="button"
            className="lk-side__close"
            onClick={onMobileClose}
            aria-label="Закрыть меню"
          >
            <CloseIcon size={16} />
          </button>
        )}
      </div>

      <WorkspaceSwitcher />

      <nav className="lk-nav">
        {navGroups.map((group) => {
          const GroupIco = group.icon;
          return (
            <div className="lk-nav__group" key={group.label}>
              <div className="lk-nav__heading">
                <GroupIco className="lk-nav__heading-ico" size={13} stroke={2} />
                {group.label}
              </div>
              {group.items.map((item) => {
                const ItemIco = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/overview'}
                    onClick={onMobileClose}
                    className={({ isActive }) =>
                      cn('lk-nav__item', isActive && 'lk-nav__item--active')
                    }
                  >
                    <ItemIco className="lk-nav__item-ico" size={15} stroke={1.75} />
                    {item.label}
                    {item.badge && (
                      <span
                        className="lk-nav__badge"
                        style={
                          item.badge === 'NEW'
                            ? {
                                background: 'var(--lk-accent-soft)',
                                color: 'var(--lk-accent-700)',
                                padding: '1px 6px',
                                borderRadius: 4,
                                fontSize: 9,
                                fontWeight: 600,
                              }
                            : undefined
                        }
                      >
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* logout() clears the token + user; <RequireAuth> reactively redirects to /login. */}
      <div className="lk-side__foot">
        <div className="lk-avatar">{userInitials(user)}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="lk-side__foot-name lk-truncate">
            {user?.email || 'Пользователь'}
          </div>
          <div className="lk-side__foot-mail lk-truncate">
            {isAdmin ? 'Администратор' : 'Аккаунт'}
          </div>
        </div>
        <button
          type="button"
          className="lk-side__foot-btn"
          title="Выйти"
          aria-label="Выйти"
          onClick={logout}
        >
          <LogoutIcon size={14} />
        </button>
      </div>
    </aside>
  );
}
